import { isSameDay, isSameMonth, stepDay, incrementToFirstOfMonth } from '@tesla/design-system';
import cx from 'classnames';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useCalendarGrid } from '../index.js';
import { Day, DaysOfWeek, MonthHeader } from './index.js';
import { dateToIsoDateString, getDatePickerDate, isDateSelected } from './utils/index.js';
import { isDateNotWithinRange } from './utils/is-date-not-within-range.js';
export const DatePicker = ({ ariaLabelDayPicker = 'Use arrow keys to increment the date', ariaLabelMonthPicker = 'Use left or right arrow keys to increment the month', className, date: selectedDate, dateType, density, disabled, error, firstDayOfWeek = 0, locale = 'en-US', onBlur, onDateChange, overlapStatusIndicators, min, max, statusIndicators, ...rest }) => {
    // Create a fallback to still render the correct month and year,
    // even if no date is passed in at first (allows for no default selected date)
    const date = getDatePickerDate(selectedDate, dateType);
    const isoDateString = date.toISOString();
    const [month, setMonth] = useState(date);
    const [focusedDate, setFocusedDate] = useState(null);
    // Update Date Picker Month when date is updated from the text input field
    useEffect(() => {
        setMonth(new Date(isoDateString));
    }, [isoDateString]);
    useLayoutEffect(() => {
        if (focusedDate) {
            setMonth((prevState) => {
                if (isSameMonth(focusedDate, prevState)) {
                    return prevState;
                }
                const updatedMonth = new Date(prevState);
                updatedMonth.setDate(1);
                updatedMonth.setMonth(focusedDate < prevState ? prevState.getMonth() - 1 : prevState.getMonth() + 1);
                return updatedMonth;
            });
        }
    }, [focusedDate]);
    // Create a list of days with some data about each one, only when the month changes
    const days = useCalendarGrid(month, firstDayOfWeek);
    const handleDayKeyDown = (e, day) => {
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            setFocusedDate(stepDay(day, 1));
        }
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            setFocusedDate(stepDay(day, -1));
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedDate(stepDay(day, 7));
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedDate(stepDay(day, -7));
        }
    };
    const prevMonth = incrementToFirstOfMonth(month, -1);
    const nextMonth = incrementToFirstOfMonth(month, 1);
    const handleDateSelect = (day) => {
        const newSelectedDay = isDateSelected(selectedDate, dateType) && isSameDay(day, date) ? null : day;
        if (dateType === 'string') {
            const isoDateString = dateToIsoDateString(newSelectedDay);
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(isoDateString);
        }
        else {
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(newSelectedDay);
        }
    };
    const handleStepMonth = (e) => {
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            setFocusedDate(null);
            setMonth(nextMonth);
        }
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            setFocusedDate(null);
            setMonth(prevMonth);
        }
    };
    return (React.createElement("div", { className: cx('tds-date-picker', { [`tds-density--${density}`]: density }, className), ...rest },
        React.createElement(MonthHeader, { ariaLabelMonthPicker: ariaLabelMonthPicker, date: month, locale: locale, onKeyDown: handleStepMonth, onNextClick: () => {
                setFocusedDate(null);
                setMonth(nextMonth);
            }, onPrevClick: () => {
                setFocusedDate(null);
                setMonth(prevMonth);
            }, min: min, max: max }),
        React.createElement(DaysOfWeek, { firstDayOfWeek: firstDayOfWeek, locale: locale }),
        React.createElement("div", { "aria-label": ariaLabelDayPicker, className: cx('tds-date-picker-days-grid', {
                'tds-date-picker-days-grid--with-indicators': !!statusIndicators,
            }), onBlur: (e) => {
                if (!e.currentTarget.contains(e.relatedTarget) &&
                    !e.target.classList.contains('tds-day--not-this-month')) {
                    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
                }
            } }, days.map((day) => {
            return (React.createElement(Day, { date: day, error: isSameDay(day, date) && error, focused: isSameDay(day, focusedDate), isDisabled: disabled || isDateNotWithinRange(day, min, max), key: `${day.getDate()}_${day.getMonth()}`, month: month, onDateSelect: (e, date) => {
                    handleDateSelect(date);
                    e.currentTarget instanceof HTMLElement && e.currentTarget.blur();
                }, onKeyDown: (e) => handleDayKeyDown(e, new Date(day)), overlapStatusIndicators: overlapStatusIndicators, selected: isDateSelected(selectedDate, dateType) && isSameDay(day, date), statusIndicators: statusIndicators }));
        }))));
};
